import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../common/types';
import type { SwitchWorkspaceState } from './useSwitchWorkspace';
import { WorkspaceSwitcherRow } from './WorkspaceSwitcherRow';
import { WorkspaceSwitchError } from './WorkspaceSwitchError';
import { workspaceMessages } from './workspaceMessages';
import { Panel } from '../music24/components/primitives/Panel';
import styles from './WorkspaceSwitcherSection.module.scss';

interface WorkspaceSwitcherSectionProps {
  /** `menu`: inside the sidebar artist switcher dropdown (a1). `sheet`: the mobile account sheet (b1). */
  variant: 'menu' | 'sheet';
  /** From `useSwitchWorkspace` of the menu or the sheet, which also dims its artists (a3). */
  switcher: SwitchWorkspaceState;
  /** Closes the menu or the sheet. A click on the active workspace calls it (a1). */
  onClose?: () => void;
}

/**
 * design: workspaces a1–a4, b1. Nothing with one workspace (a2): the menu stays the artist
 * switcher of today. The workspace part appears as soon as the list has two rows, and it stays
 * while a switch runs or an alert shows.
 */
const WorkspaceSwitcherSectionComponent: React.FC<WorkspaceSwitcherSectionProps> = ({ variant, switcher, onClose }) => {
  const intl = useIntl();
  const { rows, isVisible, switchTo, pendingLicenseId, failure } = switcher;

  const select = useCallback(
    (licenseId: number) => {
      if (rows.some((workspace) => workspace.licenseId === licenseId && workspace.isActive)) {
        onClose?.();
        return;
      }
      switchTo(licenseId);
    },
    [onClose, rows, switchTo]
  );

  if (!isVisible) {
    return null;
  }
  const header = intl.formatMessage(workspaceMessages.header);
  const rowList = rows.map((workspace) => (
    <WorkspaceSwitcherRow
      key={workspace.licenseId}
      workspace={workspace}
      variant={variant}
      isPending={pendingLicenseId === workspace.licenseId}
      isDisabled={pendingLicenseId !== null && pendingLicenseId !== workspace.licenseId}
      onSelect={select}
    />
  ));
  return (
    <div role="group" aria-label={header}>
      <div className={clsx(styles.header, variant === 'sheet' && styles.sheetHeader)}>
        <span>{header}</span>
        {pendingLicenseId === null ? (
          <span className={styles.figure}>{intl.formatMessage(workspaceMessages.count, { count: rows.length })}</span>
        ) : (
          <span className={styles.opening}>{intl.formatMessage(workspaceMessages.opening)}</span>
        )}
      </div>
      {variant === 'sheet' ? (
        <Panel nested className={styles.cell}>
          {rowList}
        </Panel>
      ) : (
        <div>{rowList}</div>
      )}
      {failure !== null && (
        <WorkspaceSwitchError
          failure={failure}
          workspaceName={failure.workspaceName}
          onRetry={() => switchTo(failure.licenseId)}
        />
      )}
    </div>
  );
};

export const WorkspaceSwitcherSection = TypedMemo(WorkspaceSwitcherSectionComponent);
